/* eslint-disable */
export class WorkerPool {
    private workers: Worker[] = [];
    private busy = new Set<Worker>();
    private queue: Array<{ job: any; onCov: (x: any, y?: any) => void; resolve: (any: any) => void; reject: (e: any) => void }> = [];
    private _booted!: Promise<void>;
    private sourceInjected = false;
    private size: number;
    private insurers: any = {};
    private memoryLimit: number = 100 * 1024 * 1024;
    private memoryUsage: Map<Worker, number> = new Map();
    private messageHandlers: Map<Worker, (e: MessageEvent) => void> = new Map();
    private workerJobs: Map<Worker, any> = new Map();

    constructor(
        private workerUrl: string,
        private remoteEngineUrl: string,
        private buffer?: ArrayBuffer,
        public sizeOrPercentage?: number
    ) {
        const hwThreads = navigator.hardwareConcurrency || 4;

        if (sizeOrPercentage === undefined) {
            this.size = Math.min(Math.max(Math.floor(hwThreads / 3), 4), 8);
        } else if (sizeOrPercentage > 0 && sizeOrPercentage <= 1) {
            this.size = Math.min(Math.max(Math.floor(hwThreads * sizeOrPercentage), 1), hwThreads);
        } else {
            this.size = Math.min(Math.max(Math.floor(sizeOrPercentage), 1), hwThreads);
        }
        this._booted = this.boot();
    }

    booted(): Promise<void> {
        return this._booted;
    }

    async injectSource(engineSource: string): Promise<void> {
        if (this.sourceInjected) return;
        const buffer = new TextEncoder().encode(engineSource).buffer;
        const jobs = this.workers.map(w => {
            const clone = buffer.slice(0);
            return new Promise<void>(res => {
                const h = (e: MessageEvent) => {
                    if (e.data.type === 'sourceReady') {
                        w.removeEventListener('message', h);
                        res();
                    }
                };
                w.addEventListener('message', h);
                w.postMessage({runId: -4, type: 'setSource', source: clone}, [clone]);
            });
        });
        await Promise.all(jobs);
        this.sourceInjected = true;
    }

    private async boot(): Promise<void> {
        for (let i = 0; i < this.size; i++) {
            const w = new Worker(this.workerUrl);
            (w as any).wid = `w-${i}`;

            const messageHandler = (e: MessageEvent) => this.onMsg(w, e);
            w.addEventListener('message', messageHandler);
            this.messageHandlers.set(w, messageHandler);

            this.workers.push(w);
        }
    }

    broadcastBuffer(buffer: ArrayBuffer): void {
        this.workers.forEach(w => {
            const clone = buffer.slice(0);
            w.postMessage({runId: -3, type: 'setBuffer', buffer: clone}, [clone]);
        });
    }

    broadcastStaticBuffer(buffer: ArrayBuffer): void {
        this.workers.forEach(w => {
            const clone = buffer.slice(0);
            w.postMessage({runId: -3, type: 'setStatic', buffer: clone}, [clone]);
        });
    }

    private onMsg(w: Worker, e: MessageEvent) {
        const job = this.workerJobs.get(w);
        if (!job) return;

        const {type, payload, error, insurerId, coverage} = e.data;
        if (type === 'log') {
            console.log('[Worker ' + (w as any).wid + ']', e.data.message);
            return;
        }
        if (type === 'coverage') {
            job.onCov(coverage);
            return;
        }
        if (type === 'done') {
            this.release(w);
            job.resolve(payload);
            return;
        }
        if (type === 'error') {
            this.release(w);
            job.reject(error);
            return;
        }

        if (type === 'done' || type === 'error') {
            this.memoryUsage.delete(w);
        }
    }

    private release(w: Worker) {
        this.busy.delete(w);
        this.workerJobs.delete(w);
        this.memoryUsage.delete(w);

        const next = this.queue.shift();
        if (next) {
            const free = this.workers.find(w => !this.busy.has(w));
            if (free) this.run(free, next);
        }
    }

    setInsurers(arr) {
        this.insurers = arr;
    }

    exec(
        jobData: { method: string; payload: any },
        onCov: (cov: any) => void
    ): Promise<Record<string, any>> {
        const {insurerIds, packCategories = [], packRestrictions = [], ...rest} = jobData.payload;

        const jobs: Array<{ insurerId: number; packId: number }> = [];
        for (const iid of insurerIds) {
            const i = this.insurers[iid];
            if (!i) continue;
            if (!i.active && i.active !== undefined) continue;
            if (!i.packs || i.packs.length === 0) continue;
            for (const pack of i.packs) {
                if (!pack.enable) continue;
                if (packCategories && Array.isArray(packCategories) && packCategories.length > 0 && !packCategories.includes(pack.category.slug.toUpperCase()) && !packCategories.includes(pack.id)) {
                    continue;
                }
                if (packRestrictions && Array.isArray(packRestrictions) && packRestrictions.length > 0 && !packRestrictions.includes(pack.code.toUpperCase())  && !packRestrictions.includes(pack.id)) {
                    continue;
                }
                jobs.push({insurerId: iid, packId: pack.id});
            }
        }

        const promises = jobs.map(({insurerId, packId}) =>
            new Promise<void>((res, rej) => {
                const miniPayload = {...rest, insurerId: insurerId, packId};
                const job = {job: {method: jobData.method, payload: miniPayload}, onCov, resolve: res, reject: rej};
                const free = this.workers.find(w => !this.busy.has(w));
                free ? this.run(free, job) : this.queue.push(job);
            })
        );
        return Promise.all(promises).then(() => ({}));
    }

    execPerInsurer(
        jobData: { method: string; payload: any },
        onResult: (result: any) => void
    ): Promise<void> {
        const {insurerIds, ...rest} = jobData.payload;
        const promises = insurerIds.map((insurerId: string) =>
            new Promise<void>((res) => {
                const miniPayload = {...rest, insurerId};
                const job = {
                    job: {method: jobData.method, payload: miniPayload, type: 'calculatePerInsurer'},
                    onCov: onResult,
                    resolve: res,
                    reject: (err: any) => {
                        onResult({insurerId, premium: 0, error: err?.message || String(err), errorStack: err?.stack ? String(err.stack) : null, details: [], errors: []});
                        res();
                    }
                };
                const free = this.workers.find(w => !this.busy.has(w));
                free ? this.run(free, job) : this.queue.push(job);
            })
        );
        return Promise.all(promises).then(() => {});
    }

    private async run(w: Worker, job: any) {
        this.busy.add(w);
        this.workerJobs.set(w, job);
        const {method, payload, type: jobType} = job.job;
        w.postMessage({runId: Date.now(), type: jobType || 'calculate', method, payload});
    }

    private checkMemoryUsage(): boolean {
        const totalUsage = Array.from(this.memoryUsage.values()).reduce((a, b) => a + b, 0);
        return totalUsage < this.memoryLimit * this.size;
    }

    terminate(): void {
        // Nettoyage explicite des handlers et références
        this.workers.forEach(w => {
            const handler = this.messageHandlers.get(w);
            if (handler) {
                w.removeEventListener('message', handler);
                this.messageHandlers.delete(w);
            }

            w.terminate();
            w.onmessage = null;
            w.onerror = null;
            w.onmessageerror = null;
        });

        this.workers = [];
        this.busy.clear();
        this.queue = [];
        this.sourceInjected = false;
        this.workerJobs.clear();
        this.memoryUsage.clear();
        this.messageHandlers.clear();
        this.insurers = {};

        const win = window as any
        if (typeof win !== 'undefined' && win.gc) {
            try {
                win.gc();
            } catch (e) {
                console.warn('[WorkerPool] GC failed:', e);
            }
        }
    }
}